/**
 * useBillingTabFacts
 *
 * 100% Central-sourced billing for Settings → Billing & Credits.
 *
 *   GET  ${CENTRAL_URL}/api/subscription/me      (plan / status / period)
 *   GET  ${CENTRAL_URL}/api/wallet/balance       (credit balance)
 *   POST ${CENTRAL_URL}/api/subscriptions/portal (Stripe portal URL)
 *
 * All paths come from @levelup/central-billing constants. No BeHeard
 * local billing endpoint is called from this tab — Central is the
 * single source of truth.
 *
 * Bearer-only. No cookies. No proxy.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import {
  createCentralPortalSession,
  getSubscription,
  getWalletBalance,
  planNameFor,
  type SubscriptionInfo,
} from '../services/centralBillingApi';
import type { BillingFacts } from '../adapters/settingsAdapter';

interface UseBillingTabFactsArgs {
  canManage: boolean;
}

interface UseBillingTabFactsReturn {
  facts: BillingFacts;
  openPortal: () => Promise<void>;
}

export function useBillingTabFacts(
  args: UseBillingTabFactsArgs,
): UseBillingTabFactsReturn {
  const token = useSelector(selectCentralAccessToken);

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  // null = not known (not loaded, unreadable, or no session) — never 0.
  const [creditsTotal, setCreditsTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      // No Central session: nothing can be read, so nothing is claimed.
      setIsLoading(false);
      setHasError(true);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    Promise.all([getSubscription(token), getWalletBalance(token)])
      .then(([sub, balance]) => {
        if (cancelled) return;
        setSubscription(sub);
        setCreditsTotal(balance);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const openPortal = useCallback(async () => {
    if (!token) return;
    setPortalLoading(true);
    try {
      const url = await createCentralPortalSession(token, window.location.href);
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      console.error('[Billing] Central portal session failed:', err);
    } finally {
      setPortalLoading(false);
    }
  }, [token]);

  const planKey = subscription?.planCode;

  const facts: BillingFacts = {
    planKey,
    planName: planNameFor(planKey) ?? undefined,
    status: subscription?.status,
    currentPeriodEnd: subscription?.currentPeriodEnd ?? null,
    cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd ?? false,
    creditsTotal,
    isLoading: isLoading || portalLoading,
    hasError,
    canManage: args.canManage,
  };

  return { facts, openPortal };
}
